const DB_NAME = 'intimsync-web-media-cache';
const DB_VERSION = 1;
const STORE = 'encrypted_blobs';

type CachedEncryptedBlob = {
  mediaId: string;
  ciphertextBase64: string;
  mimeType: string;
  createdAt: number;
  lastUsedAt: number;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'mediaId' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexeddb_open_failed'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | undefined> {
  const db = await openDb();

  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      const req = fn(store);

      let resolved = false;

      if (req) {
        req.onsuccess = () => {
          resolved = true;
          resolve(req.result);
        };
        req.onerror = () => reject(req.error || new Error('indexeddb_request_failed'));
      }

      tx.oncomplete = () => {
        if (!resolved) resolve(undefined);
      };

      tx.onerror = () => reject(tx.error || new Error('indexeddb_tx_failed'));
      tx.onabort = () => reject(tx.error || new Error('indexeddb_tx_aborted'));
    });
  } finally {
    db.close();
  }
}

export async function getCachedEncryptedMediaBlob(mediaId: string) {
  const id = String(mediaId || '').trim();
  if (!id) return null;

  try {
    const hit = await withStore<CachedEncryptedBlob>('readwrite', (store) => store.get(id));
    if (!hit?.ciphertextBase64) return null;

    const updated = {
      ...hit,
      lastUsedAt: Date.now(),
    };

    await withStore('readwrite', (store) => {
      store.put(updated);
    });

    return updated;
  } catch {
    return null;
  }
}

export async function putCachedEncryptedMediaBlob(args: {
  mediaId: string;
  ciphertextBase64: string;
  mimeType?: string;
}) {
  const mediaId = String(args.mediaId || '').trim();
  const ciphertextBase64 = String(args.ciphertextBase64 || '').trim();

  if (!mediaId || !ciphertextBase64) return false;

  try {
    const now = Date.now();

    await withStore('readwrite', (store) => {
      store.put({
        mediaId,
        ciphertextBase64,
        mimeType: String(args.mimeType || ''),
        createdAt: now,
        lastUsedAt: now,
      });
    });

    return true;
  } catch {
    return false;
  }
}

export async function deleteCachedEncryptedMediaBlob(mediaId: string) {
  const id = String(mediaId || '').trim();
  if (!id) return false;

  try {
    await withStore('readwrite', (store) => {
      store.delete(id);
    });

    return true;
  } catch {
    return false;
  }
}

export async function pruneOldEncryptedMediaBlobs(maxAgeMs = 0) {
  try {
    const db = await openDb();

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const req = store.openCursor();
      if (!maxAgeMs || maxAgeMs <= 0) {
        resolve();
        return;
      }

      const cutoff = Date.now() - maxAgeMs;

      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;

        const value = cursor.value as CachedEncryptedBlob;
        if (Number(value?.lastUsedAt || value?.createdAt || 0) < cutoff) {
          cursor.delete();
        }

        cursor.continue();
      };

      req.onerror = () => reject(req.error || new Error('indexeddb_cursor_failed'));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('indexeddb_tx_failed'));
    });

    db.close();
  } catch {
    // non-fatal
  }
}

export async function hasCachedEncryptedMediaBlob(mediaId: string) {
  const id = String(mediaId || '').trim();
  if (!id) return false;

  try {
    const hit = await withStore<CachedEncryptedBlob>('readonly', (store) => store.get(id));
    return !!hit?.ciphertextBase64;
  } catch {
    return false;
  }
}

export async function listCachedEncryptedMediaBlobIds() {
  try {
    const db = await openDb();

    return await new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const req = store.openCursor();
      const ids: string[] = [];

      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        const value = cursor.value as CachedEncryptedBlob;
        if (value?.mediaId && value?.ciphertextBase64) {
          ids.push(String(value.mediaId));
        }
        cursor.continue();
      };

      req.onerror = () => reject(req.error || new Error('indexeddb_cursor_failed'));
      tx.oncomplete = () => resolve(ids);
      tx.onerror = () => reject(tx.error || new Error('indexeddb_tx_failed'));
    }).finally(() => {
      db.close();
    });
  } catch {
    return [];
  }
}
